import toast from "react-hot-toast";
import {
  Crown,
  UserPlus,
  Users2,
  Frown,
  MessageSquare,
  Timer,
  UserX,
  Star,
  Edit2,
  Trash2,
  GripVertical,
  Save,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useUsers } from "../hooks/useUsers";
import { useUserPerformance } from "../hooks/useUserPerformance";
import { formatDuration } from "../utils/helpers";
import {
  formatScore,
  scoreColor,
} from "../components/analysis/analysisLabels";
import ConfirmModal from "../modal/ConfirmModal";
import UserModal from "../modal/UserModal";
import { useAuth } from "../context/AuthContext";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const PERIODS = [
  { value: 7, label: "7 dias" },
  { value: 30, label: "30 dias" },
  { value: 90, label: "90 dias" },
];

const EMPTY_STATS = {
  handledConversations: 0,
  assignedConversations: 0,
  openConversations: 0,
  messagesSent: 0,
  replies: 0,
  medianResponseSeconds: null,
  analyzedConversations: 0,
  agentScore: null,
  dissatisfied: 0,
  critical: 0,
  unresolved: 0,
};

const StatCard = ({ icon: Icon, tone, value, label, hint }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-900">
    <div className="mb-1 flex items-center justify-between">
      <Icon className={`h-5 w-5 ${tone}`} />
      <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        {value}
      </span>
    </div>
    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
    {hint && (
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
    )}
  </div>
);

const SortableTableRow = ({
  user,
  stats = EMPTY_STATS,
  leadEnabled,
  handleSetLeader,
  handleEditUser,
  handleDeleteUser,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: user.id, disabled: leadEnabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
    >
      <td className="whitespace-nowrap px-6 py-4">
        <div className="flex items-center">
          {!leadEnabled && (
            <button
              {...attributes}
              {...listeners}
              className="mr-3 cursor-grab touch-none text-gray-400 hover:text-gray-600 active:cursor-grabbing dark:text-gray-500 dark:hover:text-gray-300"
            >
              <GripVertical className="h-5 w-5" />
            </button>
          )}

          <div
            className={`mr-3 flex h-10 w-10 items-center justify-center rounded-full font-medium text-white ${user.leadUser ? "bg-yellow-500" : "bg-blue-500"} `}
          >
            {user.avatar ? (
              <img
                src={process.env.REACT_APP_URL + user.avatar}
                alt="Preview"
                className="h-full w-full rounded-full object-cover"
              />
            ) : (
              user.name
                .split(" ")
                .map((n) => n[0])
                .slice(0, 2)
                .join("")
                .toUpperCase()
            )}
          </div>

          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {user.name}
            </span>

            {user.leadUser && (
              <span className="mt-0.5 w-fit rounded bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                Líder
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
        {user.leadOrder}
      </td>
      <td className="whitespace-nowrap px-6 py-4">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {stats.handledConversations}
        </span>
        <span className="block text-xs text-gray-500 dark:text-gray-400">
          {stats.openConversations} abertas na carteira
        </span>
      </td>

      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
        {stats.messagesSent.toLocaleString("pt-BR")}
      </td>

      <td className="whitespace-nowrap px-6 py-4">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {formatDuration(stats.medianResponseSeconds)}
        </span>
        <span className="block text-xs text-gray-500 dark:text-gray-400">
          {stats.replies} respostas
        </span>
      </td>

      <td className="whitespace-nowrap px-6 py-4">
        <div className="flex items-center">
          <Star className={`h-4 w-4 ${scoreColor(stats.agentScore)}`} />
          <span className={`ml-1 text-sm font-semibold ${scoreColor(stats.agentScore)}`}>
            {formatScore(stats.agentScore)}
          </span>
        </div>
        <span className="block text-xs text-gray-500 dark:text-gray-400">
          {stats.analyzedConversations} analisadas
        </span>
      </td>

      <td className="whitespace-nowrap px-6 py-4">
        <div className="flex flex-wrap gap-1">
          {stats.critical > 0 && (
            <span
              className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300"
              title="Conversas em nível crítico"
            >
              {stats.critical} crítica{stats.critical > 1 ? "s" : ""}
            </span>
          )}
          {stats.dissatisfied > 0 && (
            <span
              className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
              title="Clientes avaliados como insatisfeitos"
            >
              {stats.dissatisfied} insatisfeito{stats.dissatisfied > 1 ? "s" : ""}
            </span>
          )}
          {stats.unresolved > 0 && (
            <span
              className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
              title="Conversas pendentes ou com resolução parcial"
            >
              {stats.unresolved} sem resolver
            </span>
          )}
          {stats.critical === 0 && stats.dissatisfied === 0 && stats.unresolved === 0 && (
            <span className="text-xs text-gray-400">—</span>
          )}
        </div>
      </td>
      <td className="whitespace-nowrap px-6 py-4">
        <div className="flex items-center space-x-2">
          {leadEnabled && !user.leadUser && (
            <button
              onClick={() => handleSetLeader(user)}
              className="rounded p-1 text-yellow-600 transition-colors hover:bg-yellow-50 dark:text-yellow-400 dark:hover:bg-yellow-900/20"
              title="Definir como líder"
            >
              <Crown className="h-4 w-4" />
            </button>
          )}

          <button
            onClick={() => handleEditUser(user)}
            className="rounded p-1 text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
            title="Editar"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDeleteUser(user.id)}
            className="rounded p-1 text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
            title="Excluir"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const Users = () => {
  const { user } = useAuth();

  const {
    users,
    setUsers,
    addUser,
    updateUser,
    deleteUser,
    setLeader,
    saveOrder,
    loading,
    error,
  } = useUsers();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [days, setDays] = useState(30);

  const { performance, loading: loadingPerformance } = useUserPerformance(days);

  const statsById = useMemo(
    () => new Map((performance?.items || []).map((item) => [item.id, item])),
    [performance],
  );

  const team = performance?.team || {
    activeUsers: 0,
    messagesSent: 0,
    medianResponseSeconds: null,
    replies: 0,
    conversations: 0,
    analyzedConversations: 0,
    agentScore: null,
    dissatisfied: 0,
    critical: 0,
    unattributedMessages: 0,
  };

  const leadEnabled = users.some((u) => u.leadUser);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const openUserModal = () => {
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const handleSaveUser = async (userData) => {
    try {
      if (selectedUser) {
        await updateUser(selectedUser.id, userData);
        toast.success("Usuário atualizado com sucesso!");
      } else {
        await addUser(userData);
        toast.success("Usuário adicionado com sucesso!");
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Ocorreu um erro ao salvar o usuário.");
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleDeleteUser = (userId) => {
    setUserToDelete(userId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (userToDelete) {
        await deleteUser(userToDelete);
        toast.success("Usuário excluído com sucesso!");
        setUserToDelete(null);
        setConfirmOpen(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao excluir o usuário.");
    }
  };

  const toggleLeads = async () => {
    try {
      if (leadEnabled) {
        const user = users.find((u) => u.leadUser);
        await setLeader(user.id, false);
        toast.success("Leads automáticos ativados");
      } else {
        const firstUser = users[0];
        if (firstUser) {
          await setLeader(firstUser.id, true);
          toast.success(`${firstUser.name} definido como líder`);
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao atualizar configuração de leads.");
    }
  };

  const handleSetLeader = async (user) => {
    try {
      if (user) {
        await setLeader(user.id, true);
        toast.success(`${user.name} é o novo líder.`);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao excluir ao definir líder.");
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setUsers((prev) => {
      const oldIndex = prev.findIndex((u) => u.id === active.id);
      const newIndex = prev.findIndex((u) => u.id === over.id);

      const reordered = arrayMove(prev, oldIndex, newIndex);

      return reordered.map((user, index) => ({
        ...user,
        leadOrder: index + 1,
      }));
    });

    setHasOrderChanges(true);
  };

  const handleSaveOrder = async () => {
    try {
      await saveOrder(users);
      toast.success("Ordem salva com sucesso!");
      setHasOrderChanges(false);
    } catch (err) {
      toast.error("Erro ao salvar a ordem");
    }
  };

  if (loading) return <p>Carregando usuários...</p>;
  if (error) return <p className="text-red-500">Erro: {error}</p>;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6 transition-colors duration-300 dark:bg-gray-800">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Gestão de Usuários
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Desempenho apurado sobre as conversas com atividade nos últimos{" "}
              {days} dias
              {loadingPerformance && " · atualizando..."}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-gray-300 p-0.5 dark:border-gray-600">
              {PERIODS.map((period) => (
                <button
                  key={period.value}
                  onClick={() => setDays(period.value)}
                  className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                    days === period.value
                      ? "bg-green-500 text-white"
                      : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                  }`}
                >
                  {period.label}
                </button>
              ))}
            </div>

            <button
              onClick={openUserModal}
              className="rounded-lg bg-green-500 px-4 py-2 text-white transition-colors hover:bg-green-600"
            >
              <UserPlus className="mr-2 inline h-4 w-4" />
              Novo Usuário
            </button>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <StatCard
            icon={Users2}
            tone="text-blue-500"
            value={users.length}
            label="Usuários"
            hint={`${team.activeUsers} atenderam no período`}
          />
          <StatCard
            icon={MessageSquare}
            tone="text-green-500"
            value={team.messagesSent.toLocaleString("pt-BR")}
            label="Mensagens enviadas"
            hint={`${team.conversations} conversas com atividade`}
          />
          <StatCard
            icon={Timer}
            tone="text-indigo-500"
            value={formatDuration(team.medianResponseSeconds)}
            label="Resposta (mediana)"
            hint={`${team.replies.toLocaleString("pt-BR")} respostas medidas`}
          />
          <StatCard
            icon={Star}
            tone="text-purple-500"
            value={
              <span className={scoreColor(team.agentScore)}>
                {formatScore(team.agentScore)}
              </span>
            }
            label="Nota da IA"
            hint={`${team.analyzedConversations} conversas analisadas`}
          />
          <StatCard
            icon={Frown}
            tone="text-orange-500"
            value={team.dissatisfied}
            label="Clientes insatisfeitos"
            hint={`${team.critical} conversas críticas`}
          />
          <StatCard
            icon={UserX}
            tone="text-gray-500"
            value={team.unattributedMessages.toLocaleString("pt-BR")}
            label="Envios sem autoria"
            hint="Respondidas fora do CRM"
          />
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-colors duration-300 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Lista de Usuários
            </h2>

            <div className="flex items-center gap-4">
              {/* Switch */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Ativar Leads
                </span>

                <button
                  onClick={toggleLeads}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    leadEnabled
                      ? "bg-gray-300 dark:bg-gray-600"
                      : "bg-green-500"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      leadEnabled ? "translate-x-1" : "translate-x-6"
                    }`}
                  />
                </button>

                {!leadEnabled && hasOrderChanges && (
                  <button
                    onClick={handleSaveOrder}
                    className="rounded-lg bg-green-500 px-4 py-2 text-white transition-colors hover:bg-green-600"
                  >
                    <Save className="mr-2 inline h-4 w-4" />
                    Salvar Ordem
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Usuário
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Ordem Lead
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Conversas
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Mensagens
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                      title="Mediana do tempo entre a mensagem do cliente e a primeira resposta, ignorando intervalos acima de 24h"
                    >
                      Resposta
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Nota da IA
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Alertas
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                  <SortableContext
                    items={users.map((u) => u.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {users.map((user) => (
                      <SortableTableRow
                        key={user.id}
                        user={user}
                        stats={statsById.get(user.id)}
                        leadEnabled={leadEnabled}
                        handleSetLeader={handleSetLeader}
                        handleEditUser={handleEditUser}
                        handleDeleteUser={handleDeleteUser}
                      />
                    ))}
                  </SortableContext>
                </tbody>
              </table>
            </DndContext>
          </div>
        </div>
      </div>

      {/* Modal */}
      <UserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveUser}
        activeUser={user}
        user={selectedUser}
      />

      <ConfirmModal
        isOpen={confirmOpen}
        title="Excluir Usuário"
        message="Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita."
        onConfirm={confirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default Users;
